import GoogleMapReact from 'google-map-react'
import React, { FC, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import CardWrapper from './style/CardWrapper.style'
import Wrapper from './style/Wrapper.style'
import { Lot } from '../../types/lot.interface'
import { MapLocation } from '../../types/mapLocation.interface'
import LotCard from '../LotCard/LotCard.component'
import Marker from './Marker'
import { useFetchLots } from '../../hooks/useFetchLots.hook'
import useOutsideClick from '../../hooks/useOutsideClick'
import { LotContext } from '../../context/LotContext/lot.context'
import { lotRoute } from '../../views/Lot/route'
import useLanguage from '@hooks/useLanguage.hook'

// munchen
const DEFAULT_LAT = 48.137154
const DEFAULT_LNG = 11.576124
const GOOGLE_MAPS_KEY = process.env.VITE_GOOGLE_MAPS_KEY || ''

interface LotsMapProps {
    publicLots: { id: string; latitude: number; longitude: number }[]
    searchedLocation?: MapLocation | null
    isMobile?: boolean
    setLotMapSelected: (lotMapSelected: boolean) => void
}

const LotsMap = ({ publicLots, searchedLocation, isMobile, setLotMapSelected }: LotsMapProps) => {
    const [lng] = useLanguage()
    const history = useHistory()
    const { fetchLotData } = useFetchLots()
    const { modifyAndSetLot } = useContext(LotContext)
    const [mapPosition, setMapPosition] = useState({ lat: DEFAULT_LAT, lng: DEFAULT_LNG })
    const [selectedLot, setSelectedLot] = useState<Lot | null>(null)
    const [map, setMap] = useState<any | null>(null)
    const [zoomLevel, setZoomLevel] = useState<number>(6)

    const markers: MapLocation[] = publicLots.map((lot) => ({
        lotId: lot.id,
        lat: lot.latitude,
        lng: lot.longitude
    }))

    const ref = useOutsideClick(() => {
        setLotMapSelected(false)
        setSelectedLot(null)
    })

    useEffect(() => {
        if (!searchedLocation || !map) return

        setMapPosition(searchedLocation)
        setZoomLevel(12)
        map.panTo(searchedLocation)
    }, [searchedLocation, map])

    const onMarkerClick = async (lotId: string) => {
        const lot = publicLots.find((lot) => lot.id === lotId)
        if (!lot) return

        if (isMobile) {
            setSelectedLot((lot as Lot) || null)
            setLotMapSelected(true)
        } else {
            const newLot = await fetchLotData(lotId)
            if (newLot) {
                modifyAndSetLot(newLot)
                history.push(lotRoute(lotId.toLowerCase()))
            }
        }
    }

    if (!GOOGLE_MAPS_KEY) return null

    return (
        <Wrapper>
            <GoogleMapReact
                bootstrapURLKeys={{ key: GOOGLE_MAPS_KEY, language: lng, libraries: ['places'] }}
                center={{ lat: mapPosition.lat, lng: mapPosition.lng }}
                zoom={zoomLevel}
                yesIWantToUseGoogleMapApiInternals
                onGoogleApiLoaded={({ map }) => {
                    setMap(map)
                }}
            >
                {markers.map((marker) => (
                    <Marker
                        id={marker.lotId}
                        key={marker.lotId}
                        lat={marker.lat}
                        lng={marker.lng}
                        onMarkerClick={onMarkerClick}
                    />
                ))}
            </GoogleMapReact>

            {selectedLot && (
                <CardWrapper ref={ref}>
                    <LotCard lot={selectedLot} />
                </CardWrapper>
            )}
        </Wrapper>
    )
}

export default LotsMap
